import { getChatAPI } from '@/api/ChatApi';
import { GetThreadResponseBody } from '@/api/ApiResponse';
import { GQL_USER_FRAGMENT, GQL_USER_FRAGMENT_NAME, User } from '@/models/User';
import { client as gqlClient } from '@/api/GraphQLClient';
import { MessageFromApi } from '@/models/Message';
import { setCurrentUser } from '@/store/reducers/UserStore';
import { ThreadFromApi } from '@/models/Thread';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { gql } from '@apollo/client';

export const fetchLimitedInitialChatState = createAsyncThunk(
  'data/fetchLimitedInitialChatState',
  async (_, { rejectWithValue }) => {
    try {
      const chatApi = getChatAPI();
      const { data: threadsData } = await chatApi.getUserThreads();

      return threadsData;
    } catch (threadsError) {
      if (threadsError instanceof Error) {
        return rejectWithValue({ errorMessage: threadsError.message });
      }
      throw threadsError;
    }
  },
);

export const fetchInitialChatState = createAsyncThunk(
  'data/fetchInitialChatState',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await gqlClient.query({
        query: GET_INITIAL_STATE_QUERY,
      });
      const me = data?.me ?? null;

      if (!me) {
        throw new Error('No user data');
      }

      const sender: User = {
        uuid: me.uuid,
        displayName: me.displayName,
      };
      dispatch(setCurrentUser(sender));

      const threads = getThreadsFromGraphQL(data);

      return { threads };
    } catch (threadsError) {
      if (threadsError instanceof Error) {
        return rejectWithValue({ errorMessage: threadsError.message });
      }

      throw threadsError;
    }
  },
);

export const fetchThreadState = createAsyncThunk<GetThreadResponseBody, string>(
  'data/fetchThreadState',
  async (threadId: string, { rejectWithValue }) => {
    try {
      const chatApi = getChatAPI();
      const { data: threadData } = await chatApi.getThread(threadId);

      return threadData;
    } catch (threadError) {
      if (threadError instanceof Error) {
        return rejectWithValue({ errorMessage: threadError.message });
      }

      throw threadError;
    }
  },
);

const GET_INITIAL_STATE_QUERY = gql`
  ${GQL_USER_FRAGMENT}

  query GetInitialState {
    me {
      ...${GQL_USER_FRAGMENT_NAME}
      webChannel {
        threads {
          edges {
            node {
              id
              users {
                ...${GQL_USER_FRAGMENT_NAME}
              }
              latestMessage: messages(last: 1) {
                edges {
                  node {
                    id
                    text
                    annotatedText
                    sender {
                      ...${GQL_USER_FRAGMENT_NAME}
                    }
                    timestamp
                    hasToolCalls
                  }
                }
              }
              widgetInView: events(types: [UI_STATE], last: 1) {
                edges {
                  node {
                    dataJSON
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export function getThreadsFromGraphQL(data: any): ThreadFromApi[] {
  const threadsFromGraphQL =
    data.me.webChannel?.threads?.edges?.map((edge: any) => edge.node) ?? null;

  const threads: ThreadFromApi[] = threadsFromGraphQL.reduce(
    (allThreads: ThreadFromApi[], node: any) => {
      const widgetInView = JSON.parse(
        node?.widgetInView?.edges?.[0]?.node?.dataJSON ?? 'null',
      );

      const msg = node.latestMessage?.edges?.[0]?.node ?? null;
      const latestMessage: MessageFromApi | null = msg
        ? {
            id: msg.id,
            text: msg.annotatedText,
            stripped_text: msg.text,
            structured_text: msg.annotatedText,
            sender: {
              uuid: msg.sender.uuid,
              display_name: msg.sender.displayName,
            },
            timestamp: msg.timestamp,
            has_tool_calls: msg.hasToolCalls,
          }
        : null;

      // only include threads with messages
      if (!latestMessage) {
        return allThreads;
      }

      const thread: ThreadFromApi = {
        key: node.id,
        name: node.id,
        messages: latestMessage ? [latestMessage] : [],
        users: node.users.map((user: any) => {
          return {
            uuid: user.uuid,
            display_name: user.displayName,
          };
        }),
        ui_state: {
          widgets_in_view: widgetInView ? [widgetInView] : [],
        },
      };

      return allThreads.concat(thread);
    },
    [],
  );

  return threads;
}
